import type { AIContext, AIContextField } from '../context/ai-context.js';
import { comparable } from './normalize.js';

/** Puntajes deterministas por tipo de coincidencia (mayor = mas especifica). */
const SCORE = {
  label: 100,
  synonym: 95,
  id: 90,
  dictionary: 85,
  contains: 50,
  contained: 40,
  description: 10,
} as const;

export type FieldKindFilter = AIContextField['kind'] | undefined;

export type FieldMatchOutcome =
  | { readonly status: 'matched'; readonly field: AIContextField; readonly score: number }
  | { readonly status: 'ambiguous'; readonly candidates: readonly AIContextField[] }
  | { readonly status: 'none'; readonly suggestions: readonly AIContextField[] };

interface Term {
  readonly text: string;
  readonly tokens: readonly string[];
  readonly score: number;
}

export interface MemberMatch {
  readonly dimensionId: string;
  /** Valor canonico tal como figura entre los miembros conocidos. */
  readonly value: string | number | boolean;
}

export interface FieldMatcher {
  /** Resuelve una frase a un campo visible; nunca inventa campos. */
  match(phrase: string, kind?: FieldKindFilter): FieldMatchOutcome;
  /** Miembros conocidos cuyo valor coincide con la frase (sin acentos ni mayusculas). */
  matchMember(phrase: string, dimensionId?: string): MemberMatch[];
  field(id: string): AIContextField | undefined;
}

const toTerm = (text: string, score: number): Term | null => {
  const normalized = comparable(text);
  return normalized === '' ? null : { text: normalized, tokens: normalized.split(' '), score };
};

/**
 * Indice de coincidencias sobre el contexto de IA: etiquetas, nombres, sinonimos, ids,
 * terminos del diccionario y, como ultimo recurso, descripciones. Solo considera campos
 * presentes en el contexto (visibles), por lo que nunca resuelve objetos ocultos.
 *
 * @example
 * createFieldMatcher(context).match('ventas', 'measure'); // { status: 'matched', field: Ventas }
 */
export function createFieldMatcher(context: AIContext): FieldMatcher {
  const termsByField = new Map<string, Term[]>();
  const descriptionTokens = new Map<string, Set<string>>();
  for (const field of context.fields) {
    const tail = field.id.slice(field.id.lastIndexOf('.') + 1).replace(/_/g, ' ');
    const terms = [
      toTerm(field.label, SCORE.label),
      toTerm(field.name, SCORE.label),
      toTerm(tail, SCORE.id),
      ...field.synonyms.map((synonym) => toTerm(synonym, SCORE.synonym)),
    ].filter((term): term is Term => term !== null);
    termsByField.set(field.id, terms);
    if (field.description !== undefined) {
      descriptionTokens.set(field.id, new Set(comparable(field.description).split(' ')));
    }
  }
  for (const entry of context.dictionary) {
    for (const id of entry.relatedObjectIds) {
      const term = toTerm(entry.term, SCORE.dictionary);
      if (term) termsByField.get(id)?.push(term);
    }
  }
  const fieldsById = new Map(context.fields.map((field) => [field.id, field]));
  const membersByDimension = new Map(
    context.members.map((entry) => [
      entry.dimensionId,
      entry.values.map((value) => ({ value, text: comparable(String(value)) })),
    ]),
  );

  const scoreField = (field: AIContextField, phrase: string, tokens: readonly string[]): number => {
    let best = 0;
    for (const term of termsByField.get(field.id) ?? []) {
      if (term.text === phrase) best = Math.max(best, term.score);
      else if (tokens.every((token) => term.tokens.includes(token))) {
        best = Math.max(best, SCORE.contains);
      } else if (
        term.tokens.every((token) => tokens.includes(token)) &&
        tokens.length - term.tokens.length <= 2
      ) {
        best = Math.max(best, SCORE.contained);
      }
    }
    if (best === 0) {
      const description = descriptionTokens.get(field.id);
      if (description && tokens.every((token) => description.has(token))) best = SCORE.description;
    }
    return best;
  };

  return {
    match(phrase, kind) {
      const normalized = comparable(phrase);
      if (normalized === '') return { status: 'none', suggestions: [] };
      const tokens = normalized.split(' ');
      let top = 0;
      let candidates: AIContextField[] = [];
      for (const field of context.fields) {
        if (kind !== undefined && field.kind !== kind) continue;
        const score = scoreField(field, normalized, tokens);
        if (score === 0) continue;
        if (score > top) {
          top = score;
          candidates = [field];
        } else if (score === top) {
          candidates.push(field);
        }
      }
      const [only] = candidates;
      if (only && candidates.length === 1) return { status: 'matched', field: only, score: top };
      if (candidates.length > 1) return { status: 'ambiguous', candidates };
      const suggestions = context.fields
        .filter((field) => kind === undefined || field.kind === kind)
        .filter((field) =>
          (termsByField.get(field.id) ?? []).some((term) =>
            term.tokens.some((token) => tokens.some((t) => t.length >= 3 && token.startsWith(t))),
          ),
        )
        .slice(0, 5);
      return { status: 'none', suggestions };
    },
    matchMember(phrase, dimensionId) {
      const normalized = comparable(phrase);
      if (normalized === '') return [];
      const matches: MemberMatch[] = [];
      for (const [id, members] of membersByDimension) {
        if (dimensionId !== undefined && id !== dimensionId) continue;
        const hit = members.find((member) => member.text === normalized);
        if (hit) matches.push({ dimensionId: id, value: hit.value });
      }
      return matches;
    },
    field: (id) => fieldsById.get(id),
  };
}
